/**
 * @fileoverview WEB-831 — single source of truth for the uppercase
 * `N EVENT(S)` chrome fragment.
 *
 * Three surfaces hand-rolled this fragment independently (the home-feed day
 * header, the `/discover` day section, the category-filter count caption), and
 * the third shipped in review WITHOUT pluralization — a single-event row read
 * `SHOWING 1 EVENTS`. Nothing structural caught it. Routing every caller
 * through one helper makes that class of bug unrepresentable.
 *
 * Uppercased in the string rather than via CSS `text-transform` so exact-text
 * assertions stay deterministic in jsdom (CSS casing does not alter text nodes).
 */
import { formatCount } from './count';

export type EventCountCasing = 'upper' | 'sentence';

export interface FormatEventCountOptions {
  /**
   * Word inserted between the number and the noun, e.g. `'hidden'` →
   * `"12 hidden events"`. Passed through verbatim — callers own its casing.
   */
  readonly qualifier?: string;
  /**
   * WEB-1189 C15 — public meta lines use `'sentence'` (`"18 events"`).
   * `'upper'` stays the default so admin and signed-in callers that already
   * assert the shouted form keep it.
   */
  readonly casing?: EventCountCasing;
  /**
   * Thousands-separate the number (`1234` → `"1,234"`). Opt-in, because the
   * day-grouped surfaces render bare digits today and this helper must not
   * change their output.
   *
   * Thousands-separation goes through the shared `formatCount` (FRE-1328),
   * which owns the pinned-locale reasoning: the runtime default of a bare
   * `toLocaleString()` can differ between the Node SSR render and the browser,
   * producing a hydration mismatch on a digit separator.
   */
  readonly separateThousands?: boolean;
}

function eventCountNoun(count: number, casing: EventCountCasing): string {
  const singular = count === 1;
  switch (casing) {
    case 'upper':
      return singular ? 'EVENT' : 'EVENTS';
    case 'sentence':
      return singular ? 'event' : 'events';
    default: {
      const unreachable: never = casing;
      return unreachable;
    }
  }
}

/**
 * Formats an event count, singularizing at exactly one.
 * `formatEventCount(1)` → `"1 EVENT"`, `formatEventCount(18, { casing: 'sentence' })` → `"18 events"`.
 */
export function formatEventCount(count: number, options?: FormatEventCountOptions): string {
  const { qualifier, separateThousands = false, casing = 'upper' } = options ?? {};
  const noun = eventCountNoun(count, casing);
  const formattedCount = separateThousands ? formatCount(count) : String(count);
  return qualifier ? `${formattedCount} ${qualifier} ${noun}` : `${formattedCount} ${noun}`;
}
